import { useEffect } from "react";
import type { AuthModel } from "../app/modules/auth/core/_models";
import { envConfig, requestPermission } from "../utils/config";
import { resolveNumericUserId } from "../utils/notificationUserId";
import { notificationSocket } from "../services/notification.socket";
import { notificationEcho } from "../services/notification.echo";

type NotificationTransport = {
    connect: () => void;
    disconnect: () => void;
};

export const useNotificationSocket = (
    auth: AuthModel | undefined
) => {
    const sanctumToken = auth?.api_token;
    const legacyToken = auth?.user_id;
    const numericUserId = resolveNumericUserId(auth);

    useEffect(() => {
        let transport: NotificationTransport | null = null;

        /**
         * Laravel Echo + Reverb
         */
        if (envConfig.USE_REVERB_NOTIFICATIONS) {
            if (!sanctumToken || !numericUserId) {
                return;
            }

            transport = {
                connect: () => {
                    notificationEcho.connect(
                        sanctumToken,
                        numericUserId
                    );
                },

                disconnect: () => {
                    notificationEcho.disconnect();
                },
            };
        }

        /**
         * Legacy Node WebSocket
         */
        else {
            if (!legacyToken) {
                return;
            }

            transport = {
                connect: () => {
                    notificationSocket.connect(legacyToken);
                },

                disconnect: () => {
                    notificationSocket.disconnect();
                },
            };
        }

        requestPermission();

        /**
         * Initial connection
         */
        transport.connect();

        /**
         * Cleanup
         *
         * This is NOT triggered by tab switching.
         * It only happens when this effect is destroyed,
         * such as logout/auth change/component unmount.
         */
        return () => {
            transport?.disconnect();
        };
    }, [sanctumToken, legacyToken, numericUserId]);
};